import {Link, useLocation} from 'react-router-dom'
import clsx from 'clsx'
import {useMemo} from 'react'
import {useAuth} from '../../../../app/modules/auth'
import {removeModuleAuthRoles} from '../../../../app/modules/auth/core/AuthHelpers'
import {buildModuleNavigation, flattenModuleNavigation} from '../../../../app/platform/navigation'
import {MODULE_PRESENTATION, useModule} from '../../../../app/platform/modules'
import {KTSVG} from '../../../helpers'
import {UserMenu} from '../../../partials'

const getInitials = (name?: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return 'N'
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toLocaleUpperCase('tr-TR')
}

const ModuleRail = () => {
  const {auth, currentUser} = useAuth()
  const {clearModule, defaultRoute, module, moduleCode} = useModule()
  const {pathname} = useLocation()
  const activeRoleName = auth?.role || currentUser?.roleName
  const isLegacyAssessmentPreview =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_ALLOW_LEGACY_MODULE_PREVIEW === 'true' &&
    moduleCode === 'assessment' &&
    typeof auth?.userRoleId === 'number' &&
    auth.userRoleId < 0

  const leaveLegacyPreview = () => {
    if (!moduleCode) {
      return
    }

    removeModuleAuthRoles(moduleCode)
    clearModule()
    window.location.assign('/dashboard')
  }

  const primaryNavigationItem = useMemo(() => {
    if (!moduleCode) {
      return undefined
    }

    const navigation = buildModuleNavigation({
      defaultRoute,
      menus: currentUser?.menus,
      moduleCode,
      roleName: activeRoleName,
    })
    const leaves = flattenModuleNavigation(navigation).filter(
      (item) => item.to && item.to !== defaultRoute
    )

    return leaves.find((item) => item.source === 'permission') || leaves[0]
  }, [activeRoleName, currentUser?.menus, defaultRoute, moduleCode])

  if (!module || !moduleCode) {
    return null
  }

  const presentation = MODULE_PRESENTATION[moduleCode]
  const isDashboard = pathname.startsWith('/module-dashboard')
  const isPrimaryNavigationActive = Boolean(
    primaryNavigationItem?.to && pathname.startsWith(primaryNavigationItem.to)
  )

  return (
    <aside className='nosi-module-rail' aria-label='Modül hızlı erişim alanı'>
      <Link
        className='nosi-module-rail__brand'
        to={defaultRoute}
        aria-label='NOSİ modül ana sayfası'
      >
        <span aria-hidden='true'>N</span>
      </Link>

      <nav className='nosi-module-rail__nav' aria-label='Modül kısayolları'>
        <Link
          to={defaultRoute}
          className={clsx('nosi-module-rail__button', {active: isDashboard})}
          title='Genel Bakış'
          aria-label='Genel Bakış'
          aria-current={isDashboard ? 'page' : undefined}
        >
          <KTSVG path='/media/icons/duotune/abstract/abs042.svg' className='svg-icon-2' />
        </Link>

        {primaryNavigationItem?.to && (
          <Link
            to={primaryNavigationItem.to}
            className={clsx('nosi-module-rail__button', {
              active: !isDashboard && isPrimaryNavigationActive,
            })}
            title={primaryNavigationItem.title || presentation.shortName}
            aria-label={`${module.displayName} yetkili çalışma alanı`}
            aria-current={!isDashboard && isPrimaryNavigationActive ? 'page' : undefined}
          >
            <KTSVG path={primaryNavigationItem.icon || presentation.icon} className='svg-icon-2' />
          </Link>
        )}

        {isLegacyAssessmentPreview && (
          <button
            type='button'
            className='nosi-module-rail__button nosi-module-rail__button--exit'
            title='Genel sisteme dön'
            aria-label='Genel sisteme dön'
            onClick={leaveLegacyPreview}
          >
            <i className='bi bi-arrow-left' aria-hidden='true'></i>
          </button>
        )}
      </nav>

      <div className='nosi-module-rail__footer'>
        <button
          type='button'
          className='nosi-module-rail__avatar border-0 p-0'
          title={currentUser?.fullName || currentUser?.fullname || currentUser?.username}
          aria-label='Profilim'
          data-kt-menu-trigger='click'
          data-kt-menu-overflow='false'
          data-kt-menu-placement='top-start'
        >
          {getInitials(currentUser?.fullName || currentUser?.fullname || currentUser?.username)}
        </button>
        <UserMenu />
      </div>
    </aside>
  )
}

export {ModuleRail}
